import apiClient from '../axios.js'

/**
 * 會員管理 API
 */
export const memberService = {
    /**
     * 獲取會員列表
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @param {string} params.search - 搜尋關鍵字（姓名、電話、Email）
     * @param {string} params.status - 會員狀態（active, inactive, pending, blocked）
     * @param {string} params.tierId - 會員等級 ID
     * @param {string} params.tagIds - 標籤 ID 清單（逗號分隔）
     * @param {string} params.tagMatchType - 標籤符合方式（OR, AND）
     * @param {string} params.registeredStart - 加入會員日期起（YYYY-MM-DD）
     * @param {string} params.registeredEnd - 加入會員日期迄（YYYY-MM-DD）
     * @param {string} params.birthdayStart - 生日起（YYYY-MM-DD）
     * @param {string} params.birthdayEnd - 生日迄（YYYY-MM-DD）
     * @param {number} params.birthdayMonth - 生日月份（1-12）
     * @param {number} params.birthdayDay - 生日日期（1-31）
     * @param {string} params.addressCity - 地址縣市
     * @param {string} params.addressDistrict - 地址鄉鎮區
     * @param {string} params.addressStreet - 詳細地址
     * @param {string} params.sortBy - 排序欄位（createdAt, name, points, updatedAt）
     * @param {string} params.order - 排序方向（asc, desc）
     * @returns {Promise<Object>} 會員列表
     */
    getMembers: async (params = {}) => {
        const response = await apiClient.get('/members', { params })
        return response
    },

    /**
     * 獲取單一會員詳情
     * @param {string} id - 會員 ID
     * @returns {Promise<Object>} 會員詳情
     */
    getMember: async (id) => {
        const response = await apiClient.get(`/members/${id}`)
        return response
    },

    /**
     * 新增會員
     * @param {Object} memberData - 會員資料
     * @param {string} memberData.name - 姓名（必填）
     * @param {string} memberData.phone - 手機號碼（必填）
     * @param {string} memberData.email - Email（選填）
     * @param {string} memberData.birthday - 生日（選填，格式：YYYY-MM-DD）
     * @param {string|null} memberData.gender - 性別（選填：male, female, neutral；空白為 null）
     * @param {string} memberData.address_city - 地址城市（選填）
     * @param {string} memberData.address_district - 地址區域（選填）
     * @param {string} memberData.address_street - 地址街道（選填）
     * @param {string} memberData.address_postal_code - 郵遞區號（選填）
     * @param {string} memberData.member_tier - 會員等級（選填）
     * @param {string} memberData.status - 狀態（選填，預設 active）
     * @returns {Promise<Object>} 新增結果
     */
    createMember: async (memberData) => {
        const response = await apiClient.post('/members', memberData)
        return response
    },

    /**
     * 更新會員（完整更新）
     * @param {string} id - 會員 ID
     * @param {Object} memberData - 會員資料
     * @returns {Promise<Object>} 更新結果
     */
    updateMember: async (id, memberData) => {
        const response = await apiClient.put(`/members/${id}`, memberData)
        return response
    },

    /**
     * 後台重設會員登入密碼（不需舊密碼）
     * @param {string} id - 會員 ID
     * @param {{ newPassword: string, confirmPassword: string }} body
     */
    resetMemberPassword: async (id, body) => {
        const response = await apiClient.put(`/members/${id}/password/reset`, body)
        return response
    },

    /**
     * 部分更新會員
     * @param {string} id - 會員 ID
     * @param {Object} memberData - 會員資料（只需傳送要更新的欄位）
     * @returns {Promise<Object>} 更新結果
     */
    patchMember: async (id, memberData) => {
        const response = await apiClient.patch(`/members/${id}`, memberData)
        return response
    },

    /**
     * 刪除會員（軟刪除）
     * @param {string} id - 會員 ID
     * @returns {Promise<Object>} 刪除結果
     */
    deleteMember: async (id) => {
        const response = await apiClient.delete(`/members/${id}`)
        return response
    },

    /**
     * 根據標籤查詢會員
     * @param {Array<string>} tagIds - 標籤 ID 陣列
     * @param {number} limit - 限制筆數
     * @returns {Promise<Object>} 會員列表
     */
    getMembersByTags: async (tagIds, limit = 10000, matchType = 'OR', memberFilter = 'registered', excludeTagIds = [], lineConfigId = null) => {
        const body = { tagIds, limit, matchType, memberFilter };
        if (excludeTagIds && excludeTagIds.length > 0) body.excludeTagIds = excludeTagIds;
        if (lineConfigId) body.lineConfigId = lineConfigId;
        const response = await apiClient.post('/members/search/by-tags', body);
        return response;
    },

    /**
     * 根據多種識別方式批量查詢會員
     * @param {Array<string>} identifiers - 識別資訊陣列（電話、LINE User ID、UUID）
     * @returns {Promise<Object>} 會員列表
     */
    getMembersByIdentifiers: async (identifiers) => {
        const response = await apiClient.post('/members/search/by-identifiers', { identifiers });
        return response;
    },

    /**
     * 取得匯入 CSV 直傳 S3 的預簽名 PUT URL
     * @param {Object} uploadData - { fileName, fileType, fileSize }
     * @returns {Promise<Object>} { presignedUrl, objectKey, expiresIn }
     */
    getImportUploadUrl: async (uploadData) => {
        const response = await apiClient.post('/members/import/upload-url', uploadData);
        return response;
    },

    /**
     * 建立批次匯入工作（CSV 已直傳 S3，後端下載解析）
     * @param {string} s3Key - 原始 CSV 的 S3 物件鍵
     * @param {Object} options - 匯入選項
     * @param {string} options.duplicateStrategy - 重複處理策略（skip | merge）
     * @param {string} options.fileName - 原始檔名
     * @param {Array<string>} options.mergeFields - 合併時要更新的欄位
     * @param {Object} options.genderMap - 性別欄位對應表（原始值 -> male/female/neutral/''）
     * @param {number} options.totalRows - 本地解析筆數（僅供進度顯示）
     */
    importMembersFromS3: async (s3Key, options = {}) => {
        const body = {
            s3Key,
            duplicateStrategy: options.duplicateStrategy || 'skip',
            fileName: options.fileName || null,
            syncToWixtar: false
        };
        if (options.mergeFields !== undefined) {
            body.mergeFields = options.mergeFields;
        }
        if (options.genderMap) {
            body.genderMap = options.genderMap;
        }
        if (options.columnMapping) {
            body.columnMapping = options.columnMapping;
        }
        if (Number.isInteger(options.totalRows)) {
            body.totalRows = options.totalRows;
        }
        const response = await apiClient.post('/members/import', body);
        return response;
    },

    getImportJobs: async (params = {}) => {
        const response = await apiClient.get('/members/import-jobs', { params });
        return response;
    },

    getImportJob: async (jobNumber) => {
        const response = await apiClient.get(`/members/import-jobs/${jobNumber}`);
        return response;
    },

    previewRevokeImport: async (jobNumber) => {
        const response = await apiClient.get(`/members/import-jobs/${jobNumber}/revoke-preview`);
        return response;
    },

    executeRevokeImport: async (jobNumber) => {
        const response = await apiClient.post(`/members/import-jobs/${jobNumber}/revoke`);
        return response;
    },

    /**
     * 建立會員匯出工作（背景產出 CSV 上傳 S3）
     * @param {Object} filters - 套用的列表篩選條件（同 getMembers 參數；空物件＝全量匯出）
     * @returns {Promise<Object>} { jobNumber, status }
     */
    createExportJob: async (filters = {}) => {
        const body = Object.keys(filters).length > 0 ? { filters } : {};
        const response = await apiClient.post('/members/export', body);
        return response;
    },

    /**
     * 查詢匯出工作列表
     * @param {Object} params - { page, pageSize }
     */
    getExportJobs: async (params = {}) => {
        const response = await apiClient.get('/members/export-jobs', { params });
        return response;
    },

    /**
     * 查詢單一匯出工作（輪詢進度用）
     * @param {string} jobNumber - 匯出工作編號
     */
    getExportJob: async (jobNumber) => {
        const response = await apiClient.get(`/members/export-jobs/${jobNumber}`);
        return response;
    },

    /**
     * 取得匯出檔案下載連結：端點驗證身分與租戶後回傳短效 presigned URL（60 秒），
     * 由呼叫端以瀏覽器導航直接下載（避免 XHR 跨網域轉址的 CORS 問題）
     * @param {string} jobNumber - 匯出工作編號
     * @returns {Promise<Object>} 包含 url 與 expiresIn 的物件
     */
    getExportDownloadUrl: async (jobNumber) => {
        const response = await apiClient.get(`/members/export-jobs/${jobNumber}/download`);
        return response;
    }
}
